<script setup lang="ts">
import { onMounted, ref, watch } from "vue";

import useTranslation from "@Composables/useTranslation";
import showModal from "@Helpers/functions/showModal.js";
import SettingsTopBar from "@Modules/teams/components/SettingsTopBar.vue";
import OrgSettingsContent from "@Modules/teams/components/OrgSettingsContent.vue";
import UpdatePasswordForm from "@Modules/security-settings/Components/UpdatePasswordForm.vue";
import LinkSocialAccounts from "@Modules/security-settings/Components/LinkSocialAccounts.vue";
import TwoFactorSetup from "@Modules/security-settings/Components/TwoFactorSetup.vue";
import TwoFactorManage from "@Modules/security-settings/Components/TwoFactorManage.vue";
import TwoFactorBackupCodes from "@Modules/security-settings/Components/TwoFactorBackupCodes.vue";
import TwoFactorVerifyModal from "@Modules/security-settings/Modals/TwoFactorVerifyModal.vue";
import TwoFactorBackupCodesModal from "@Modules/security-settings/Modals/TwoFactorBackupCodesModal.vue";
import TwoFactorConfirmationModal from "@Modules/security-settings/Modals/TwoFactorConfirmationModal.vue";
import type { BackupCodesResult, UpdatePasswordParams } from "@Modules/security-settings/Api/SecurityApi";

//
// Setup
const { __ } = useTranslation();

//
// Props
interface Props {
  hasSocialAccount: (provider: string) => boolean;
  isTwoFactorEnabled: boolean;
  hasBackupCodes: boolean;
  backupCodesCreatedDate?: string | null;
  errors?: Record<string, string>;
  loading?: boolean;
  updatePassword: (params: UpdatePasswordParams) => Promise<boolean>;
  enableTwoFactor: () => Promise<boolean>;
  disableTwoFactor: () => Promise<boolean>;
  generateBackupCodes: () => Promise<BackupCodesResult | null>;
  deleteBackupCodes: () => Promise<boolean>;
  fetchBackupCodesDate: () => Promise<void>;
  getQrCodeUrl: () => Promise<string | null>;
  verifyTwoFactor: (params: { token: string }) => Promise<boolean>;
  refreshApplicationState: () => Promise<void>;
}

const props = withDefaults(defineProps<Props>(), {
  backupCodesCreatedDate: null,
  errors: () => ({}),
  loading: false,
});

//
// Data
const isEnabling2fa = ref(false);
const isDisabling2fa = ref(false);
const isGenerating = ref(false);
const isDeleting = ref(false);
const isFetching = ref(false);

//
// Methods
const showConfirmModal = (modalProps: {
  title: string;
  notice: string;
  cancel: string;
  confirm: string;
  callback: () => void;
}): void => {
  showModal(TwoFactorConfirmationModal, modalProps);
};

const onGenerateBackupCodes = async (): Promise<void> => {
  isGenerating.value = true;
  const result = await props.generateBackupCodes();

  if (result) {
    await props.refreshApplicationState();
    showModal(TwoFactorBackupCodesModal, {
      codes: result.backup_codes,
      created: result.created,
    });
  }

  isGenerating.value = false;
};

/**
 * The api resolves to false both when the server demands verification and when the
 * request genuinely failed, so a falsy result is what opens the enrollment modal.
 */
const enableTwoFactor = async (): Promise<void> => {
  isEnabling2fa.value = true;
  const success = await props.enableTwoFactor();
  isEnabling2fa.value = false;

  if (!success) {
    showModal(TwoFactorVerifyModal, {
      getQrCodeUrl: props.getQrCodeUrl,
      verifyTwoFactor: props.verifyTwoFactor,
      notifyVerified: async () => {
        await onGenerateBackupCodes();
      },
    });

    return;
  }

  await props.refreshApplicationState();
};

const disableTwoFactor = async (callback: () => void = () => {}): Promise<void> => {
  isDisabling2fa.value = true;
  const success = await props.disableTwoFactor();
  isDisabling2fa.value = false;

  if (success) {
    await props.refreshApplicationState();
    callback();
  }
};

const changeDevice = (): void => {
  showConfirmModal({
    title: __("two_factor_auth_settings.change_device_confirmation.title"),
    notice: __("two_factor_auth_settings.change_device_confirmation.notice"),
    cancel: __("two_factor_auth_settings.change_device_confirmation.cancel"),
    confirm: __("two_factor_auth_settings.change_device_confirmation.confirm"),
    callback: () => disableTwoFactor(() => enableTwoFactor()),
  });
};

const showDisableTwoFactorConfirmation = (): void => {
  showConfirmModal({
    title: __("two_factor_auth_settings.disable_two_factor_confirmation.title"),
    notice: __("two_factor_auth_settings.disable_two_factor_confirmation.notice"),
    cancel: __("two_factor_auth_settings.disable_two_factor_confirmation.cancel"),
    confirm: __("two_factor_auth_settings.disable_two_factor_confirmation.confirm"),
    callback: () => disableTwoFactor(),
  });
};

const onDeleteBackupCodes = async (): Promise<void> => {
  isDeleting.value = true;
  await props.deleteBackupCodes();
  await props.refreshApplicationState();
  isDeleting.value = false;
};

const showDeleteCodesConfirmation = (): void => {
  showConfirmModal({
    title: __("two_factor_auth_settings.delete_backup_code_confirmation.title"),
    notice: __("two_factor_auth_settings.delete_backup_code_confirmation.notice"),
    cancel: __("two_factor_auth_settings.delete_backup_code_confirmation.cancel"),
    confirm: __("two_factor_auth_settings.delete_backup_code_confirmation.confirm"),
    callback: onDeleteBackupCodes,
  });
};

const fetchBackupCodes = async (): Promise<void> => {
  if (!props.isTwoFactorEnabled || !props.hasBackupCodes) {
    return;
  }

  isFetching.value = true;
  await props.fetchBackupCodesDate();
  isFetching.value = false;
};

//
// Watchers
watch(() => props.isTwoFactorEnabled, () => {
  fetchBackupCodes();
});

//
// Lifecycle
onMounted(() => {
  fetchBackupCodes();
});

//
// Exposed Test Vars
defineExpose({ isEnabling2fa, isDisabling2fa, isGenerating, isDeleting, isFetching });
</script>

<template>
  <div class="security-settings">
    <SettingsTopBar :title="__('security_settings.page_title')" />

    <OrgSettingsContent :title="__('security_settings.update_password_title')">
      <UpdatePasswordForm
        :update-password="updatePassword"
        :errors="errors"
        :loading="loading"
      />
    </OrgSettingsContent>

    <OrgSettingsContent :title="__('security_settings.link_social_account_title')">
      <LinkSocialAccounts :has-social-account="hasSocialAccount" />
    </OrgSettingsContent>

    <OrgSettingsContent :title="__('security_settings.two_factor_auth_title')">
      <TwoFactorSetup
        v-if="!isTwoFactorEnabled"
        :is-enabling="isEnabling2fa"
        @enable="enableTwoFactor"
      />
      <TwoFactorManage
        v-else
        :is-changing-device="isEnabling2fa"
        :is-disabling="isDisabling2fa"
        @change-device="changeDevice"
        @disable="showDisableTwoFactorConfirmation"
      />
    </OrgSettingsContent>

    <OrgSettingsContent
      v-if="isTwoFactorEnabled"
      :title="__('security_settings.backup_codes_title')"
    >
      <TwoFactorBackupCodes
        :backup-codes-created-date="backupCodesCreatedDate"
        :is-generating="isGenerating"
        :is-deleting="isDeleting"
        :is-fetching="isFetching"
        @generate="onGenerateBackupCodes"
        @delete="showDeleteCodesConfirmation"
      />
    </OrgSettingsContent>
  </div>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;

.security-settings {
  $self: &;

  display: flex;
  flex-flow: column;
  gap: spacing.$s;
}
</style>
